import { notNullOrUndefined, Site } from "@equiem/web-ng-lib";
import Link from "next/link";
import React, { forwardRef, HTMLAttributes, MouseEventHandler, PropsWithChildren, useContext, useMemo } from "react";

export type ButtonProps = {
  className?: string;
  contrast?: boolean;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  rel?: string;
  rounded?: boolean;
  roundedRadius?: string;
  borderWidth?: string;
  size?: "sm" | "lg";
  style?: HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>["style"];
  tabIndex?: number;
  target?: string;
  variant: "solid" | "outline" | "link";
  onMouseOver?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseOut?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  prefetch?: boolean;
} & (
    { href: string; as?: string; type?: undefined } | { href?: undefined; as?: undefined; type?: "button" | "submit" | "reset"; }
  );

export const Button = forwardRef<any, PropsWithChildren<ButtonProps>>(({
  children,
  className: inputClassName = "",
  contrast = false,
  disabled = false,
  onClick,
  rel,
  rounded = true,
  roundedRadius = "4px",
  borderWidth = "1px",
  size,
  style,
  tabIndex = 0,
  target,
  variant,
  onMouseOver,
  onMouseOut,
  prefetch = true,
  ...rest
}, ref) => {
  const site = useContext(Site);

  const primaryColor = site.primaryColour;
  const contrastColor = site.primaryContrastColour;
  const whiteContrastColor = site.whiteContrastColour;

  const typeClassName = useMemo(() => `btn-${variant}`, [variant]);

  const className = useMemo(() => `${[
    "btn",
    typeClassName,
    disabled ? "disabled" : null,
    size != null ? `btn-${size}` : null,
  ].filter(notNullOrUndefined).join(" ")} ${inputClassName
    }`,
    [variant, inputClassName, disabled, size],
  );

  const borderRadius = rounded ? roundedRadius : "0";
  const hoverShadow = variant === "link" ? undefined : "0 0 10px rgba(0, 0, 0, 0.3)";

  const styles = useMemo(() => {
    switch (variant) {
      case "solid": return {
        btn: `
          background: ${contrast ? contrastColor : primaryColor};
          color: ${contrast ? primaryColor : contrastColor};
          border-radius: ${borderRadius};
        `,
        hover: `
          box-shadow: ${hoverShadow};
        `,
      };
      case "outline": return {
        btn: `
          color: ${contrast ? contrastColor : whiteContrastColor};
          border-radius: ${borderRadius};
          border: ${borderWidth} solid ${contrast ? contrastColor : primaryColor};
        `,
        hover: `
          box-shadow: ${hoverShadow};
        `,
      };
      case "link": return {
        btn: `
          color: ${contrast ? contrastColor : whiteContrastColor};
          font-weight: 600;
          text-decoration: underline;
        `,
        hover: `
          color: ${contrast ? contrastColor : whiteContrastColor};
        `,
      };
    }
  }, [variant, contrast]);

  return (
    <span className="btn-root" style={{ display: "contents" }}>
      {"href" in rest && rest.href != null ? (
        <Link
          onClick={onClick}
          className={className}
          style={style}
          tabIndex={tabIndex}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          href={rest.href}
          as={rest.as}
          target={target}
          rel={rel}
          ref={ref}
          prefetch={prefetch}>
          {children}
        </Link>
      ) : (
        <button
          onClick={onClick}
          type={rest.type}
          className={className}
          style={style}
          tabIndex={tabIndex}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          disabled={disabled}
          ref={ref}>
          {children}
        </button>
      )}
      <style jsx>{`
        .btn-root :global(.btn),
        .btn-root :global(a.btn:active),
        .btn-root :global(a.btn:visited) {
          ${styles.btn}
        }
        .btn-root :global(.btn:hover:not(:disabled):not(.disabled)),
        .btn-root :global(.btn:focus:not(:disabled):not(.disabled)) {
          ${styles.hover}
        }
        .btn-root :global(.btn.disabled),
        .btn-root :global(.btn:disabled) {
          opacity: .5;
        }
      `}</style>
    </span>
  );
});
